import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Youtube, Play, ArrowUpRight, Calendar, Sparkles } from "lucide-react";
import { BRAND } from "@/constants/brand";

const CACHE_KEY = "bc-yt-cache-v1";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expires || Date.now() > parsed.expires) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, expires: Date.now() + CACHE_TTL_MS }),
    );
  } catch {
    /* ignore */
  }
};

const resolveChannelId = async (apiKey, handle, storedChannelId) => {
  if (storedChannelId) return storedChannelId;
  const cachedId = localStorage.getItem("bc-yt-channel-id");
  if (cachedId) return cachedId;
  if (!handle) return null;
  const cleanHandle = handle.startsWith("@") ? handle : `@${handle}`;
  const url = `https://www.googleapis.com/youtube/v3/channels?part=id,contentDetails&forHandle=${encodeURIComponent(
    cleanHandle,
  )}&key=${apiKey}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to resolve channel handle");
  const json = await res.json();
  const item = json.items?.[0];
  if (!item) throw new Error("Channel not found");
  const id = item.id;
  const uploadsId = item.contentDetails?.relatedPlaylists?.uploads;
  if (id) localStorage.setItem("bc-yt-channel-id", id);
  if (uploadsId) localStorage.setItem("bc-yt-uploads-id", uploadsId);
  return id;
};

const fetchUploadsId = async (apiKey, channelId) => {
  const cached = localStorage.getItem("bc-yt-uploads-id");
  if (cached) return cached;
  const url = `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${channelId}&key=${apiKey}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch channel details");
  const json = await res.json();
  const uploadsId = json.items?.[0]?.contentDetails?.relatedPlaylists?.uploads;
  if (uploadsId) localStorage.setItem("bc-yt-uploads-id", uploadsId);
  return uploadsId;
};

const fetchLatestVideos = async (apiKey, playlistId, max = 6) => {
  const url = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&maxResults=${max}&playlistId=${playlistId}&key=${apiKey}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch videos (HTTP ${res.status})`);
  const json = await res.json();
  return (json.items || []).map((it) => {
    const s = it.snippet;
    const thumbs = s?.thumbnails || {};
    const thumb =
      thumbs.maxres?.url || thumbs.standard?.url || thumbs.high?.url || thumbs.medium?.url || thumbs.default?.url;
    return {
      id: it.contentDetails?.videoId || it.id,
      title: s?.title || "Untitled",
      publishedAt: s?.publishedAt,
      thumbnail: thumb,
    };
  });
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const DummyCard = ({ idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: idx * 0.06 }}
    className="group relative overflow-hidden rounded-2xl border border-border bg-card/40"
    data-testid={`youtube-dummy-card-${idx}`}
  >
    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[hsl(var(--brand-navy)/0.15)] to-[hsl(var(--brand-orange)/0.12)]">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 opacity-70">
          <div className="rounded-full bg-[hsl(var(--brand-orange)/0.15)] p-4">
            <Play size={22} className="text-[hsl(var(--brand-orange))]" fill="currentColor" />
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Video coming soon
          </span>
        </div>
      </div>
    </div>
    <div className="space-y-2 p-4">
      <div className="h-3 w-3/4 rounded-full bg-muted animate-pulse" />
      <div className="h-3 w-1/2 rounded-full bg-muted animate-pulse" />
    </div>
  </motion.div>
);

const VideoCard = ({ video, idx }) => (
  <motion.a
    href={`https://www.youtube.com/watch?v=${video.id}`}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: idx * 0.06 }}
    className="group relative overflow-hidden rounded-2xl border border-border bg-card/40 lift"
    data-testid={`youtube-video-card-${idx}`}
  >
    <div className="relative aspect-video overflow-hidden">
      {video.thumbnail ? (
        <img
          src={video.thumbnail}
          alt={video.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="h-full w-full bg-muted" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90" />
      <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(0_90%_55%)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
        Video
      </div>
      <div className="absolute right-3 top-3 rounded-full bg-white/95 p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <Play size={16} className="text-[hsl(0_90%_55%)]" fill="currentColor" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="line-clamp-2 font-display text-sm font-bold leading-snug text-white">
          {video.title}
        </p>
      </div>
    </div>
    <div className="flex items-center justify-between px-4 py-3">
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Calendar size={12} /> {formatDate(video.publishedAt)}
      </span>
      <ArrowUpRight
        size={16}
        className="text-muted-foreground transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[hsl(var(--brand-orange))]"
      />
    </div>
  </motion.a>
);

const YouTubeSection = () => {
  const [videos, setVideos] = useState(() => readCache() || []);
  const [status, setStatus] = useState(videos.length ? "ready" : "idle"); // idle | loading | ready | error | unconfigured

  useEffect(() => {
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
    const channelIdEnv = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;
    const handle = process.env.REACT_APP_YOUTUBE_CHANNEL_HANDLE;

    if (!apiKey) {
      setStatus("unconfigured");
      return;
    }
    // If we have fresh cache we're done
    if (videos.length) return;

    let cancelled = false;
    const run = async () => {
      setStatus("loading");
      try {
        const channelId = await resolveChannelId(apiKey, handle, channelIdEnv);
        if (!channelId) throw new Error("Channel not found");
        const uploadsId = await fetchUploadsId(apiKey, channelId);
        if (!uploadsId) throw new Error("No uploads playlist");
        const data = await fetchLatestVideos(apiKey, uploadsId, 6);
        if (cancelled) return;
        setVideos(data);
        writeCache(data);
        setStatus(data.length ? "ready" : "unconfigured");
      } catch (err) {
        if (cancelled) return;
        console.warn("[YouTube] " + err.message);
        setStatus("error");
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showDummies = status !== "ready" || videos.length === 0;
  const dummyCount = 6;

  return (
    <section id="videos" className="section relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 grid-bg" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="chip" data-testid="videos-badge">
              <Youtube size={12} /> Latest on YouTube
            </span>
            <h2
              className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
              data-testid="videos-heading"
            >
              Fresh videos,{" "}
              <span className="text-gradient-brand">straight from the studio.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Auto-updating grid of the latest uploads from{" "}
              <span className="font-semibold text-foreground">@buggucapital</span> —
              tap any card to watch on YouTube.
            </p>
          </div>
          <a
            href={BRAND.socials.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-brand"
            data-testid="videos-visit-channel"
          >
            <Youtube size={18} /> Visit Channel
          </a>
        </div>

        {status === "error" ? (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-destructive/30 bg-destructive/10 px-4 py-2 text-xs text-destructive">
            <Sparkles size={12} /> Couldn&apos;t load latest videos right now. Showing placeholders.
          </div>
        ) : null}

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {showDummies
            ? Array.from({ length: dummyCount }).map((_, i) => <DummyCard key={i} idx={i} />)
            : videos.map((v, i) => <VideoCard key={v.id || i} video={v} idx={i} />)}
        </div>

        {/* {status === "unconfigured" ? (
          <p className="mt-8 text-center text-xs text-muted-foreground" data-testid="videos-config-hint">
            Add <code className="mx-1 rounded bg-muted px-1.5 py-0.5">REACT_APP_YOUTUBE_API_KEY</code>
            to the .env file and refresh to auto-fetch your latest uploads.
          </p>
        ) : null} */}
      </div>
    </section>
  );
};

export default YouTubeSection;
